import { MouseEvent, ReactNode } from "react";
import cx from "classnames";

import Spinner from "common/components/Spinner";

import styles from "../../styles/SelectButton.module.less";

type SelectButtonStatusProps = { isLoading?: boolean };

const SelectButtonStatus = ({ isLoading = false }: SelectButtonStatusProps) => {
  if (isLoading) {
    return <Spinner className={styles.spinner} />;
  }
  return null;
};

export type RenderContentProps = {
  status: ReactNode;
  isActive: boolean;
};

export interface SelectButtonProps {
  disabled?: boolean;
  isLoading?: boolean;
  isActive: boolean;
  renderContent?: ({ status, isActive }: RenderContentProps) => ReactNode;
  renderExpandedContent?: () => ReactNode;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  align?: "start" | "center";
  className?: string;
  buttonClassName?: string;
}

const SelectButton = (props: SelectButtonProps) => {
  const {
    isActive,
    renderContent = () => undefined,
    renderExpandedContent,
    onClick,
    disabled = false,
    align = "start",
    className = "",
    buttonClassName,
  } = props;

  return (
    <div
      className={cx(
        styles.container,
        isActive && styles.active,
        disabled && styles.disabled,
        styles[align],
        className,
      )}
    >
      <button
        type="button"
        className={cx(styles.button, buttonClassName)}
        disabled={disabled}
        onClick={onClick}
      >
        {renderContent({
          status: (
            <div className={styles.status}>
              <SelectButtonStatus {...props} />
            </div>
          ),
          isActive,
        })}
      </button>
      {isActive && renderExpandedContent && (
        <div className={styles.expanded}>{renderExpandedContent()}</div>
      )}
    </div>
  );
};

type DefinitionSelectButtonContentProps = RenderContentProps & {
  align?: "center" | "start";
  label: ReactNode;
  description?: ReactNode;
};

const DefinitionSelectButtonContent = ({
  align = "center",
  label,
  description,
  status,
}: DefinitionSelectButtonContentProps) => {
  return (
    <>
      <div className={`${styles.definitionSelectContent} ${styles[`align-${align}`]}`}>
        <div className={styles.label}>{label}</div>
        {description && <div className={styles.description}>{description}</div>}
      </div>
      {status}
    </>
  );
};

export default Object.assign(SelectButton, { DefinitionContent: DefinitionSelectButtonContent });
